import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowSquareOut,
  DownloadSimple,
  FilePdf,
  FileText,
  MagnifyingGlass,
  Minus,
  Plus,
} from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { Btn, Eyebrow } from "@/components/site/Blocks";
import { ArrowLink, Rule, SplitText } from "@/components/site/Motion";
import { API } from "@/lib/api";
import { usePageHead } from "@/lib/siteContent";
import { useReveal } from "@/lib/useReveal";

const sizeText = (n) => {
  const b = Number(n) || 0;
  if (!b) return "";
  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

// One hairline row per document (Maiko list style)
const DocRow = ({ doc, index }) => {
  const isPdf = /pdf/i.test(doc.file_type || doc.file_name || "");
  const href = doc.download ? `${API.replace(/\/api$/, "")}${doc.download}` : doc.link;
  const external = !doc.download;
  const Icon = isPdf ? FilePdf : FileText;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={`resource-${index}`}
      className="group grid items-center gap-4 border-b border-[color:var(--s-line)] py-5 transition-colors duration-300 hover:bg-[color:var(--s-surface)] sm:grid-cols-[3rem_1fr_auto] sm:gap-6"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-ink)] transition-colors duration-300 group-hover:border-[color:var(--s-gold-bright)] group-hover:bg-[color:var(--s-gold-bright)]">
        <Icon size={20} weight="regular" />
      </span>
      <span className="min-w-0">
        <span className="s-micro block text-[color:var(--s-mute)]">
          {doc.category}
          {doc.manufacturer ? ` · ${doc.manufacturer}` : ""}
        </span>
        <span className="mt-1 block text-lg font-semibold leading-snug">{doc.title}</span>
        {doc.description && (
          <span className="mt-1 block text-sm leading-relaxed text-[color:var(--s-mute)]">{doc.description}</span>
        )}
      </span>
      <span className="s-arrow-link shrink-0 !text-[10px]">
        {external ? (
          <>
            Open <ArrowSquareOut size={12} weight="bold" />
          </>
        ) : (
          <>
            {isPdf ? "PDF" : "File"}
            {doc.file_size ? ` · ${sizeText(doc.file_size)}` : ""}
            <DownloadSimple size={12} weight="bold" />
          </>
        )}
        <span className="s-arrow" />
      </span>
    </a>
  );
};

const FaqRow = ({ item, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[color:var(--s-line)]" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        className="group flex w-full items-center justify-between gap-6 py-5 text-left"
      >
        <span className="text-lg font-semibold leading-snug">{item.q}</span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-ink)] transition-colors duration-300 group-hover:border-[color:var(--s-gold-bright)] group-hover:bg-[color:var(--s-gold-bright)]">
          {open ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
        </span>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="pb-6 pr-12 text-base leading-relaxed text-[color:var(--s-mute)]" data-testid={`${testId}-answer`}>
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function SiteResources() {
  const site = useSite();
  const shop = site?.shop || {};
  const faqs = site?.config?.faqs || {};
  const [data, setData] = useState(null);
  const [maker, setMaker] = useState("");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    fetch(`${API}/public/site/resources`)
      .then((r) => (r.ok ? r.json() : { items: [], manufacturers: [], categories: [] }))
      .then(setData)
      .catch(() => setData({ items: [], manufacturers: [], categories: [] }));
  }, []);

  usePageHead({
    title: "Resources — warranties & documentation",
    description: "Manufacturer warranties, care guides, spec sheets and certificates for the stone we sell.",
  });

  const items = data?.items || [];
  const cats = useMemo(
    () => (data?.categories || []).filter((c) => items.some((d) => d.category === c)),
    [data, items],
  );
  const shown = items.filter(
    (d) =>
      (!maker || d.manufacturer === maker) &&
      (!cat || d.category === cat) &&
      (!q || `${d.title} ${d.manufacturer} ${d.description}`.toLowerCase().includes(q.toLowerCase())),
  );
  const groups = useMemo(() => {
    const m = new Map();
    shown.forEach((d) => {
      const raw = (d.manufacturer || "General").trim();
      const k = raw.toLowerCase();
      if (!m.has(k)) m.set(k, { name: raw, docs: [] });
      m.get(k).docs.push(d);
    });
    return [...m.values()].map((g) => [g.name, g.docs]);
  }, [shown]);
  useReveal([site, data]);

  return (
    <SiteShell site={site} testId="site-resources">
      {/* airy intro */}
      <section className="bg-[color:var(--s-bg)] px-6 pb-16 pt-6 sm:pt-8 lg:px-12" data-testid="resources-hero">
        <div className="mx-auto max-w-[2560px]">
          <Eyebrow>Company · Resources</Eyebrow>
          <h1 className="s-hx mt-6 break-words text-3xl sm:text-4xl lg:text-5xl" data-testid="resources-heading">
            <SplitText text="Resources." base={100} />
          </h1>
          <Rule className="mt-10 is-in" />
          <div className="mt-10 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
            <p className="text-2xl font-light leading-snug sm:text-3xl">
              Warranties, care guides &amp; technical documents for the stone we sell.
            </p>
            <div>
              <p className="max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]">
                Everything the manufacturers publish about their surfaces — warranty terms, sealing and
                cleaning guides, spec sheets and certificates — in one place. Can&apos;t find a document?
                {shop.phone ? ` Call ${shop.phone} or ` : " "}
                send us a note and we&apos;ll dig it out.
              </p>
              <ArrowLink to="/contact" className="mt-8" testId="resources-ask">
                Ask for a document
              </ArrowLink>
            </div>
          </div>
        </div>
      </section>

      {/* filters */}
      <section className="border-y border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-6 py-6 lg:px-12" data-testid="resources-filters">
        <div className="mx-auto flex max-w-[2560px] flex-col gap-5 lg:flex-row lg:items-center lg:gap-10">
          <label className="flex flex-1 items-center gap-3">
            <MagnifyingGlass size={16} className="text-[color:var(--s-mute)]" />
            <input
              data-testid="resources-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search documents…"
              className="s-line-input w-full"
            />
          </label>
          <select
            data-testid="resources-manufacturer"
            value={maker}
            onChange={(e) => setMaker(e.target.value)}
            className="s-line-input w-full lg:w-64"
          >
            <option value="">All manufacturers</option>
            {(data?.manufacturers || []).map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap gap-x-7 gap-y-2" data-testid="resources-categories">
            <button
              type="button"
              onClick={() => setCat("")}
              data-testid="resources-cat-all"
              className={`s-micro relative py-2 !text-[12px] transition-colors duration-300 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:origin-left after:scale-x-0 after:bg-[color:var(--s-ink)] after:transition-transform after:duration-300 ${
                !cat ? "text-[color:var(--s-ink)] after:scale-x-100" : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"
              }`}
            >
              All
            </button>
            {cats.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c === cat ? "" : c)}
                data-testid={`resources-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`s-micro relative py-2 !text-[12px] transition-colors duration-300 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:origin-left after:scale-x-0 after:bg-[color:var(--s-ink)] after:transition-transform after:duration-300 ${
                  cat === c ? "text-[color:var(--s-ink)] after:scale-x-100" : "text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* document groups */}
      <section className="bg-[color:var(--s-bg)] px-6 py-16 lg:px-12" data-testid="resources-list">
        <div className="mx-auto max-w-[2560px]">
          {data && !items.length && (
            <div
              className=" border border-dashed border-[color:var(--s-line)] p-12 text-center text-[color:var(--s-mute)]"
              data-testid="resources-empty"
            >
              Documents are on their way — check back soon, or{" "}
              <Link to="/contact" className="underline decoration-[color:var(--s-gold)] underline-offset-4">
                ask us directly
              </Link>
              .
            </div>
          )}
          {data && items.length > 0 && !shown.length && (
            <div className="text-[color:var(--s-mute)]" data-testid="resources-none">
              Nothing matches that filter.
            </div>
          )}

          <div className="space-y-16" data-testid="resources-groups">
            {groups.map(([name, docs]) => (
              <div key={name} data-testid={`resources-group-${slug(name)}`} className="s-reveal grid gap-8 lg:grid-cols-[1fr_2fr] lg:gap-16">
                <div>
                  <h2 className="s-hx text-3xl sm:text-4xl"><strong>{name}</strong></h2>
                  <div className="s-micro mt-3 text-[color:var(--s-mute)]">
                    {docs.length} document{docs.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="border-t border-[color:var(--s-line)]">
                  {docs.map((d, i) => (
                    <DocRow key={d.id} doc={d} index={slug(`${name}-${i}`)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      {faqs.enabled !== false && faqs.groups?.length > 0 && (
        <section className="border-t border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-6 py-20 lg:px-12" data-testid="resources-faqs">
          <div className="mx-auto max-w-[2560px]">
            <Eyebrow>Company · FAQs</Eyebrow>
            <h2 className="s-hx mt-6 break-words text-4xl sm:text-5xl lg:text-6xl" data-testid="resources-faqs-heading">
              {faqs.heading || "Frequently asked questions"}
            </h2>
            <div className="mt-14 space-y-16" data-testid="resources-faq-groups">
              {faqs.groups.map((g, gi) =>
                g.items?.length ? (
                  <div key={gi} data-testid={`resources-faq-group-${gi}`} className="s-reveal grid gap-8 lg:grid-cols-[1fr_2fr] lg:gap-16">
                    <div>
                      <h3 className="s-hx text-3xl sm:text-4xl">
                        <strong>{g.title}</strong>
                      </h3>
                    </div>
                    <div className="border-t border-[color:var(--s-line)]">
                      {g.items.map((it, ii) => (
                        <FaqRow key={ii} item={it} testId={`resources-faq-${gi}-${ii}`} />
                      ))}
                    </div>
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </section>
      )}

      {/* claim help */}
      <section className="border-t border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-6 py-20 lg:px-12" data-testid="resources-help">
        <div className="mx-auto flex max-w-[2560px] flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="s-reveal">
            <Eyebrow>Need help with a claim?</Eyebrow>
            <h2 className="s-hx mt-6 text-4xl sm:text-5xl">We&apos;ll walk you through the warranty process.</h2>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <Btn to="/contact" testId="resources-contact-cta">Contact us</Btn>
            {shop.phone && (
              <ArrowLink href={`tel:${shop.phone_link || shop.phone}`} testId="resources-phone">
                {shop.phone}
              </ArrowLink>
            )}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
